import React from 'react';
import { Controller, Control, FieldErrors, UseFormWatch } from 'react-hook-form';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';

interface HealthPlanFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    setValue?: any; // Adicionar setValue para sincronização
}

const healthProviders = ['Bradesco', 'Unimed', 'Noterdame', 'Outro'];

const HealthPlanFieldset: React.FC<HealthPlanFieldsetProps> = ({
    context,
    control,
    watch,
    errors,
    isSubmitting = false,
    setValue
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Acessar dados da família via React Hook Form
    const spouseName = watch('spouseName');
    const children = watch('children') || [];

    // Watch para campos condicionais
    const hasHealthInsurance = watch('hasHealthInsurance');

    // Construir lista de dependentes potenciais baseada nos dados da família
    const potentialDependents = React.useMemo(() => {
        const dependents: Array<{ name: string; relationship: string }> = [];

        // Adicionar cônjuge se existir
        if (spouseName && spouseName.trim()) {
            dependents.push({
                name: spouseName.trim(),
                relationship: 'Cônjuge'
            });
        }

        // Adicionar filhos
        children.forEach((child: any) => {
            if (child.name && child.name.trim()) {
                dependents.push({
                    name: child.name.trim(),
                    relationship: 'Filho(a)'
                });
            }
        });

        return dependents;
    }, [spouseName, children]);

    // Sincronizar healthDependents com os dados da família
    React.useEffect(() => {
        // Construir array de dependentes de saúde baseado nos dependentes potenciais
        const currentHealthDependents = watch('healthDependents') || [];

        // Criar nova lista de dependentes de saúde
        const newHealthDependents = potentialDependents.map(potentialDep => {
            // Verificar se já existe um dependente de saúde para esta pessoa
            const existingHealthDep = currentHealthDependents.find(
                (hd: any) => hd.name === potentialDep.name && hd.relationship === potentialDep.relationship
            );

            // Se já existe, manter os dados existentes; senão, criar novo com valores padrão
            return existingHealthDep || {
                name: potentialDep.name,
                relationship: potentialDep.relationship,
                isCovered: false,
                cardNumber: ''
            };
        });

        // Atualizar apenas se houve mudança
        if (JSON.stringify(currentHealthDependents) !== JSON.stringify(newHealthDependents)) {
            // Usar setValue para atualizar o campo healthDependents
            if (setValue) {
                setValue('healthDependents', newHealthDependents);
            }
        }
    }, [potentialDependents, control, watch, setValue]);

    // Helper local para obter layout contextual
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        switch (context) {
            case 'DP_UPDATE':
            case 'DHO_UPDATE':
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
            default: // FULL_FORM
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
        }
    };

    const layout = getLayoutByContext(context);

    // Obter healthDependents atualizado
    const healthDependents = watch('healthDependents') || [];

    // Verificar se o fieldset deve ser exibido
    if (!shouldShow('hasHealthInsurance')) {
        return null;
    }

    return (
        <fieldset className={layout.fieldsetStyle}>
            <legend className={layout.legendStyle}>Plano de Saúde</legend>
            <div className="space-y-4 pt-2">
                {/* Checkbox principal */}
                <div className="flex items-center">
                    <Controller
                        name="hasHealthInsurance"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="checkbox"
                                id="hasHealthInsurance"
                                checked={field.value || false}
                                onChange={(e) => field.onChange(e.target.checked)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                disabled={isSubmitting}
                            />
                        )}
                    />
                    <label htmlFor="hasHealthInsurance" className="ml-2 block text-sm text-gray-900">
                        Possui plano de saúde
                    </label>
                </div>

                {/* Campos condicionais quando possui plano de saúde */}
                {hasHealthInsurance && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Operadora */}
                        {shouldShow('healthInsuranceProvider') && (
                            <div>
                                <label htmlFor="healthInsuranceProvider" className="block text-sm font-medium text-gray-700">
                                    Operadora
                                </label>
                                <Controller
                                    name="healthInsuranceProvider"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <select
                                                id="healthInsuranceProvider"
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Selecione...</option>
                                                {healthProviders.map(provider => (
                                                    <option key={provider} value={provider}>{provider}</option>
                                                ))}
                                            </select>
                                            {fieldState.error && (
                                                <p className="mt-1 text-sm text-red-600" role="alert">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        )}

                        {/* Data de Inclusão */}
                        {shouldShow('healthInsuranceInclusionDate') && (
                            <div>
                                <label htmlFor="healthInsuranceInclusionDate" className="block text-sm font-medium text-gray-700">
                                    Data de Inclusão
                                </label>
                                <Controller
                                    name="healthInsuranceInclusionDate"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <input
                                                type="date"
                                                id="healthInsuranceInclusionDate"
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                            />
                                            {fieldState.error && (
                                                <p className="mt-1 text-sm text-red-600" role="alert">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        )}

                        {/* Número da Carteirinha */}
                        {shouldShow('healthInsuranceCardNumber') && (
                            <div>
                                <label htmlFor="health-card" className="block text-sm font-medium text-gray-700">
                                    Número da Carteirinha
                                </label>
                                <Controller
                                    name="healthInsuranceCardNumber"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <input
                                                type="text"
                                                id="health-card"
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                            />
                                            {fieldState.error && (
                                                <p className="mt-1 text-sm text-red-600" role="alert">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Seção de Dependentes */}
                {hasHealthInsurance && shouldShow('healthDependents') && (
                    <div className="pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-800">Dependentes</h4>
                        {healthDependents && healthDependents.length > 0 ? (
                            healthDependents.map((dependent: any, index: number) => (
                                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mt-2 p-2 border rounded-md">
                                    <div className="flex items-center">
                                        <Controller
                                            name={`healthDependents.${index}.isCovered`}
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="checkbox"
                                                    id={`dep-covered-${index}`}
                                                    checked={field.value || false}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                    className="h-4 w-4"
                                                    disabled={isSubmitting}
                                                />
                                            )}
                                        />
                                        <label htmlFor={`dep-covered-${index}`} className="ml-2 text-sm text-gray-900">
                                            {dependent?.name} <span className="text-gray-500">({dependent?.relationship})</span>
                                        </label>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label htmlFor={`dep-card-${index}`} className="sr-only">
                                            Nº Carteirinha Dependente
                                        </label>
                                        <Controller
                                            name={`healthDependents.${index}.cardNumber`}
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    id={`dep-card-${index}`}
                                                    value={field.value || ''}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    placeholder="Nº da Carteirinha do dependente"
                                                    className="input-primary w-full"
                                                    disabled={!dependent?.isCovered || isSubmitting}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">
                                Nenhum dependente (cônjuge ou filhos) cadastrado na aba 'Família'.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default HealthPlanFieldset; 